import vertexTextureQuad from "../post/quad.vert.wgsl";
import { $wgsl } from "../../wgsl-preprocessor/wgsl-preprocessor";
import dither4x4Chunk from "../chunks/dither4x4.wgsl";
import gradientShader from "./gradient.wgsl";
import { Renderer } from "../Renderer";

/** @import { TimingManager } from '../TimingManager'; */

export function GradientPass(renderer) {

  /** @type {Renderer} */
  let _renderer = renderer;
  let _device;

  // Opacity used for gradient pass output.
  let _alpha = 1.0;

  let _pipeline;
  let _passDescriptor;
  let _vShader;
  let _fShader;

  // Used to delay pipeline creation until clear colors are known.
  let _clearColorsInitialized = false;
  let _r1, _g1, _b1, _r2, _g2, _b2;

  /** @type {TimingManager|undefined} */
  let _timingManager;

  // Requires that device and shaders exists and clearConstants are known.
  function createPipeline() {
    const sampleCount = _renderer.getMSAASampleCount();
    _pipeline = _device.createRenderPipeline({
      layout: "auto",
      vertex: {
        module: _vShader,
        entryPoint: "main"
      },
      fragment: {
        module: _fShader,
        entryPoint: "main",
        targets: [
        {
          format: _renderer.getRenderTargets().getColorTarget().format,
          blend: {
            // The GradientPass overwrites destination values.
            // We output pre-multiplied alpha with a value set by pass.setBlendConstant([r, g, b, a]);
            // The shader must output unmultiplied-alpha rgb values with alpha set to 1.0.
            color: {
              operation: 'add',
              srcFactor: 'constant', // set by pass.setBlendConstant
              dstFactor: 'zero'
            },
            alpha: {
              operation: 'add',
              srcFactor: 'constant', // set by pass.setBlendConstant
              dstFactor: 'zero'
            }
          }
        }],

        constants: {
          0: _r1, 1: _g1, 2: _b1,
          3: _r2, 4: _g2, 5: _b2
        }
      },
      primitive: {
        topology: 'triangle-list',
        cullMode: 'back'
      },
      multisample: {
        count: sampleCount
      }
    });
  }

  function createResources() {

    if (!_vShader) {
      _vShader = _device.createShaderModule({
        label: 'gradient pass vertex shader',
        code: vertexTextureQuad
      });
    }

    if (!_fShader) {
      _fShader = _device.createShaderModule({
        label: 'gradient pass fragment shader',
        code: $wgsl(gradientShader, { dither4x4Chunk })
      });
    }

    // Pipeline creation fails if clear colors are undefined. Creating a throw-away pipeline with "some" values
    // wouldn't make sense either and the final values depend on viewer settings and are not known yet at this point.
    // Therefore, we defer pipeline creation as long as we have no proper values.
    if (!_pipeline && _clearColorsInitialized) {
      createPipeline();
    }

    if (!_passDescriptor) {
      _passDescriptor = {
        colorAttachments: [
        {
          // view is acquired and set in render loop.
          view: undefined,

          // Note on 'load':  Our blend settings overwrite the destination.
          // Clear is not necessary and breaks multi-viewport rendering (as it clears the whole target).
          loadOp: 'load',
          storeOp: 'store'
        }]


      };
    }

  }

  this.init = function () {
    _device = _renderer.getDevice();
    _timingManager = _renderer.getTimingManager();
    createResources();
  };

  this.setAlpha = function (alpha) {
    _alpha = alpha;
  };

  // Requires that init() was called and clear colors are set.
  this.run = function () {

    if (!_device || !_clearColorsInitialized) {
      return;
    }

    let commandEncoder = _device.createCommandEncoder();

    _passDescriptor.colorAttachments[0].view = _renderer.getRenderTargets().getColorTargetView();
    // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
    // nosemgrep
    _passDescriptor.label = 'gradient pass';
    if (_timingManager) {
      _timingManager.newPassQuery(_passDescriptor);
    }

    const pass = commandEncoder.beginRenderPass(_passDescriptor);

    _renderer.passViewport(pass);

    pass.setPipeline(_pipeline);
    pass.setBlendConstant([_alpha, _alpha, _alpha, _alpha]);
    pass.draw(3);
    pass.end();

    _device.queue.submit([commandEncoder.finish()]);

  };

  this.setClearColors = function (r1, g1, b1, r2, g2, b2) {
    _r1 = r1;
    _g1 = g1;
    _b1 = b1;

    _r2 = r2;
    _g2 = g2;
    _b2 = b2;

    _pipeline = null;
    if (_device) {
      createPipeline();
    }
    _clearColorsInitialized = true;
  };

  this.getClearColors = function () {
    return [_r1, _g1, _b1, _r2, _g2, _b2];
  };

  /** Resets the pipeline. Call when MSAA settings change. */
  this.resetPipeline = function () {
    _pipeline = null;
    if (_device && _clearColorsInitialized) {
      createPipeline();
    }
  };

}